import type {
  AvertissementGhost,
  CurrentUser,
  RemediationAction,
  RestrictionCode,
} from '@vigie/shared';

/**
 * Client d'API.
 *
 * L'authentification repose sur le cookie de session (httpOnly, SameSite=strict) : il n'y a donc
 * aucun jeton à stocker côté navigateur, et rien d'exploitable en cas de faille XSS. `credentials:
 * 'same-origin'` suffit puisque le frontend est servi par le plan de contrôle lui-même.
 */

export interface RéponseApi<T> {
  status: number;
  data: T;
}

export class ErreurApi extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ErreurApi';
  }
}

async function appel<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  url: string,
  body?: unknown,
): Promise<RéponseApi<T>> {
  const réponse = await fetch(url, {
    method,
    credentials: 'same-origin',
    headers: body === undefined ? {} : { 'content-type': 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  const texte = await réponse.text();
  const data = (texte === '' ? {} : JSON.parse(texte)) as T;
  return { status: réponse.status, data };
}

export interface ÉtatSession {
  user?: CurrentUser;
  enrollmentRequired?: boolean;
  totpRequired?: boolean;
  error?: string;
}

export const api = {
  session: () => appel<ÉtatSession>('GET', '/api/auth/me'),
  connexion: (email: string, password: string, totp?: string) =>
    appel<ÉtatSession>('POST', '/api/auth/login', {
      email,
      password,
      ...(totp === undefined ? {} : { totp }),
    }),
  validerTotp: (totp: string) => appel<ÉtatSession>('POST', '/api/auth/totp', { totp }),
  démarrerEnrôlement: () =>
    appel<{ otpauthUri: string; secretBase32: string; error?: string }>(
      'POST',
      '/api/auth/totp/enroll',
    ),
  confirmerEnrôlement: (totp: string) =>
    appel<ÉtatSession>('POST', '/api/auth/totp/enroll/confirm', { totp }),
  déconnexion: () => appel<{ ok: boolean }>('POST', '/api/auth/logout'),
};

// --- Onboarding --------------------------------------------------------------------------------

export interface EnvironnementSonde {
  os: string;
  arch: string;
  homeDir: string;
  availableBinaries: string[];
  missingBinaries: string[];
  /** Contraintes codées : le code porte la remédiation, le message sert à l'affichage brut. */
  restrictions: Array<{ code: RestrictionCode; message: string }>;
}

export interface CandidatDétection {
  installType: string;
  confidence: number;
  evidence: string[];
  suggestedParams: Record<string, string>;
}

export const onboarding = {
  démarrer: (org: string, corps: { name: string; url: string }) =>
    appel<{
      name: string;
      url: string;
      intention: string | null;
      error?: string;
      details?: unknown;
    }>('POST', `/api/organizations/${org}/onboarding/start`, corps),
  /**
   * Ce qu'on est venu faire, dit après avoir vu le serveur.
   *
   * C'est ici que l'adresse d'un site neuf est refusée si elle ne peut pas porter de certificat :
   * l'ordre des étapes a déplacé cette vérification, pas supprimée.
   */
  intention: (org: string, corps: { intention: 'existant' | 'neuf' }) =>
    appel<{ intention: string; error?: string }>(
      'POST',
      `/api/organizations/${org}/onboarding/intention`,
      corps,
    ),
  /** Abandon : efface la session d'assistant, donc la clé privée et le mot de passe qu'elle tient. */
  abandonner: (org: string) =>
    appel<{ error?: string }>('DELETE', `/api/organizations/${org}/onboarding`),
  clés: (org: string) =>
    appel<{ publicKey: string; error?: string }>(
      'POST',
      `/api/organizations/${org}/onboarding/keypair`,
    ),
  testerSsh: (
    org: string,
    corps: { sshHost: string; sshPort: number; sshUser: string; sshPassword?: string },
  ) =>
    appel<{
      hostKeyFingerprint: string;
      environment: EnvironnementSonde;
      backupMethod: 'restic' | 'streamed';
      /** Ce que la machine permet, constaté sans rien modifier — sur tous les parcours. */
      aptitude?: AptitudeCible;
      error?: string;
    }>('POST', `/api/organizations/${org}/onboarding/test-ssh`, corps),
  détecter: (org: string, corps: { ghostDir?: string; composePath?: string }) =>
    appel<{ candidates: CandidatDétection[]; best: CandidatDétection | null; error?: string }>(
      'POST',
      `/api/organizations/${org}/onboarding/detect`,
      corps,
    ),
  script: (org: string, corps: Record<string, unknown>) =>
    appel<{ script: string; warnings: string[]; error?: string }>(
      'POST',
      `/api/organizations/${org}/onboarding/ssh-user-script`,
      corps,
    ),
  remédier: (org: string, action: RemediationAction) =>
    appel<{
      action: RemediationAction;
      binary: string;
      source: 'system' | 'pushed';
      environment: EnvironnementSonde;
      backupMethod: 'restic' | 'streamed';
      error?: string;
    }>('POST', `/api/organizations/${org}/onboarding/remediate`, { action }),
  aperçuAnalyse: (org: string, corps: Record<string, unknown>) =>
    appel<{
      texte: string;
      remplacements: Record<string, number>;
      configured: boolean;
      error?: string;
    }>('POST', `/api/organizations/${org}/onboarding/assist/preview`, corps),
  analyser: (org: string, corps: Record<string, unknown>) =>
    appel<{
      analyse: string;
      texte: string;
      remplacements: Record<string, number>;
      modèle: string;
      error?: string;
    }>('POST', `/api/organizations/${org}/onboarding/assist`, corps),
  terminer: (org: string, corps: Record<string, unknown>) =>
    appel<{ site?: { id: string; name: string }; error?: string }>(
      'POST',
      `/api/organizations/${org}/onboarding/complete`,
      corps,
    ),
  /**
   * État de l'assistant, tel que le serveur le détient.
   *
   * C'est ce qui permet de reprendre après un rechargement de page : l'assistant vivait entièrement
   * dans l'état du navigateur, alors que la session serveur portait déjà l'identité du site, la
   * connexion et l'empreinte de la clé d'hôte.
   */
  état: (org: string) => appel<ÉtatAssistant>('GET', `/api/organizations/${org}/onboarding`),
  /** Dernière version stable de Ghost publiée, pour préremplir le formulaire d'installation. */
  versionGhost: (org: string) =>
    appel<{ version?: string; publiéeLe?: string; error?: string }>(
      'GET',
      `/api/organizations/${org}/onboarding/version-ghost`,
    ),
};

export interface AptitudeCible {
  uid: number;
  privileges: 'root' | 'sudo' | 'sudo_mdp_requis' | 'aucun';
  systeme: { id: string; codename: string; nom: string; prisEnCharge: boolean };
  architecture: string;
  reserves: string[];
}

export interface ÉtatAssistant {
  enCours: boolean;
  name?: string | null;
  url?: string | null;
  intention?: 'existant' | 'neuf' | null;
  sshHost?: string | null;
  sshPort?: number;
  sshUser?: string | null;
  hostKeyFingerprint?: string | null;
  moyen?: 'cle' | 'mot_de_passe';
  publicKey?: string | null;
  installationId?: string | null;
}

// --- Installation d'un site neuf ---------------------------------------------------------------

export interface EnregistrementDnsVue {
  type: 'A' | 'AAAA' | 'CNAME';
  nom: string;
  valeur: string;
}

export interface ControleDnsVue {
  le: string;
  verdict: 'correct' | 'partiel' | 'autre_adresse' | 'absent';
  resolues: { v4: string[]; v6: string[] };
  message: string;
  httpStatus?: number;
  /** Sondé seulement quand le nom pointe ici et que le site ne répond pas. */
  portsPublics?: { 80: boolean; 443: boolean };
  certificatRelanceLe?: string;
}

export interface InstallationVue {
  id: string;
  siteId: string | null;
  status: string;
  mode: 'production' | 'essai';
  siteName: string;
  url: string;
  slug: string;
  ghostVersion: string;
  www: boolean;
  sshHost: string;
  sshUser: string;
  ownerEmail: string;
  courrielConfigure: boolean;
  log: string;
  avertissements: string[];
  reprenable: boolean;
  startedAt: string;
  finishedAt: string | null;
  dns?: {
    enregistrements: EnregistrementDnsVue[];
    texte: string;
    dernierControle: ControleDnsVue | null;
  };
  error?: string;
}

export interface CorpsInstallation {
  ghostVersion: string;
  www: boolean;
  owner: { name: string; email: string; password: string };
  courriel?: { mail?: Record<string, unknown>; bulkEmail?: Record<string, unknown> };
  sudoPassword?: string;
}

export const installation = {
  lancer: (org: string, corps: CorpsInstallation) =>
    appel<{ installationId?: string; status?: string; error?: string; details?: unknown }>(
      'POST',
      `/api/organizations/${org}/installations`,
      corps,
    ),
  suivre: (org: string, id: string) =>
    appel<InstallationVue>('GET', `/api/organizations/${org}/installations/${id}`),
  actives: (org: string) =>
    appel<{ installations: InstallationVue[] }>(
      'GET',
      `/api/organizations/${org}/installations?actives=1`,
    ),
  reprendre: (org: string, id: string) =>
    appel<{ status?: string; error?: string }>(
      'POST',
      `/api/organizations/${org}/installations/${id}/reprendre`,
    ),
  vérifierDns: (org: string, id: string) =>
    appel<{ error?: string }>('POST', `/api/organizations/${org}/installations/${id}/verifier-dns`),
  terminer: (org: string, id: string) =>
    appel<{ status?: string; error?: string }>(
      'POST',
      `/api/organizations/${org}/installations/${id}/terminer`,
    ),
};

// --- Flotte ------------------------------------------------------------------------------------

export interface SiteFlotte {
  id: string;
  name: string;
  url: string;
  installType: string;
  currentStatus: 'up' | 'degraded' | 'down' | 'unknown';
  ghostVersionDetected: string | null;
  updatePolicy: string;
  backupMethod: string;
  responseTimeMs: number | null;
  lastCheckedAt: string | null;
  diskFreeBytes: number | null;
  diskTotalBytes: number | null;
  lastBackupAt: string | null;
  lastBackupStatus: string | null;
  operationInProgress: string | null;
}

export interface DétailSiteRéponse {
  site: SiteFlotte & {
    nodeVersionDetected: string | null;
    backupSchedule: string;
    monitorIntervalMinutes: number;
    restartMode: 'driver' | 'external';
    restartHint: string | null;
    sshHost: string;
    sshUser: string;
    sshHostKeyFingerprint: string | null;
  };
  alerts: Array<{ id: string; type: string; openedAt: string }>;
  history: Array<{ checkedAt: string; responseTimeMs: number | null; httpStatus: number | null }>;
  error?: string;
}

export const flotte = {
  lister: (org: string) =>
    appel<{ sites: SiteFlotte[]; error?: string }>('GET', `/api/organizations/${org}/sites`),
  détail: (org: string, siteId: string) =>
    appel<DétailSiteRéponse>('GET', `/api/organizations/${org}/sites/${siteId}`),
};

// --- Configuration -----------------------------------------------------------------------------

export type SourceConfigKind = 'json_file' | 'compose_env' | 'env_file';

export interface ValeurMasquée {
  value: string;
  /** Vrai si la valeur affichée est un masque : il faut la révéler pour la lire. */
  masked: boolean;
}

export interface OrigineClé {
  source: SourceConfigKind;
  /**
   * Faux quand la valeur de cette source est écrasée ailleurs — typiquement une clé du fichier
   * reprise par une variable d'environnement en Docker. Modifier le fichier ne changerait alors
   * rien au comportement du site.
   */
  effective: boolean;
}

export interface SourceConfig {
  kind: SourceConfigKind;
  remotePath: string;
  /** Contenu brut, secrets déjà masqués côté serveur : il alimente l'éditeur de texte. */
  rawContent: string;
  keys: Record<string, ValeurMasquée>;
  origins: Record<string, OrigineClé>;
}

export interface ChangementConfig {
  path: string;
  kind: 'added' | 'removed' | 'changed';
  before: string | null;
  after: string | null;
  masked: boolean;
}

export interface ProblèmeValidation {
  path: string;
  message: string;
}

export interface AperçuConfig {
  changes: ChangementConfig[];
  diff: string;
  overriddenPaths: string[];
  /** Ce qui restera silencieusement inactif, sans empêcher d'enregistrer. */
  warnings?: AvertissementGhost[];
  error?: string;
  issues?: ProblèmeValidation[];
}

export interface RésultatConfig {
  revisionId: string;
  changes: ChangementConfig[];
  healthy: boolean;
  rolledBack: boolean;
  /** Écrite, mais pas encore chargée : le redémarrage se fait hors de Vigie. */
  pendingRestart: boolean;
  restartHint: string | null;
  log: string[];
  error?: string;
  issues?: ProblèmeValidation[];
}

export interface RévisionConfig {
  id: string;
  source: SourceConfigKind;
  diff: string;
  authorUserId: string | null;
  createdAt: string;
  applied: boolean;
  restoredFromRevisionId: string | null;
}

export const sitesRéglages = {
  /** Déclare qui redémarre le site : Vigie, ou le panneau de l'hébergeur. */
  modeRedémarrage: (
    org: string,
    siteId: string,
    corps: { restartMode: 'driver' | 'external'; restartHint: string | null },
  ) =>
    appel<{ restartMode: string; restartHint: string | null; error?: string }>(
      'PATCH',
      `/api/organizations/${org}/sites/${siteId}/restart-mode`,
      corps,
    ),
};

export const configuration = {
  lire: (org: string, siteId: string) =>
    appel<{ sources: SourceConfig[]; error?: string }>(
      'GET',
      `/api/organizations/${org}/sites/${siteId}/config`,
    ),
  révéler: (
    org: string,
    siteId: string,
    corps: { source: SourceConfigKind; remotePath: string; path: string },
  ) =>
    appel<{ path: string; value: string; error?: string }>(
      'POST',
      `/api/organizations/${org}/sites/${siteId}/config/reveal`,
      corps,
    ),
  prévisualiser: (
    org: string,
    siteId: string,
    corps: { source: SourceConfigKind; remotePath: string; content: unknown },
  ) =>
    appel<AperçuConfig>('POST', `/api/organizations/${org}/sites/${siteId}/config/preview`, corps),
  appliquer: (
    org: string,
    siteId: string,
    corps: { source: SourceConfigKind; remotePath: string; content: unknown },
  ) => appel<RésultatConfig>('PUT', `/api/organizations/${org}/sites/${siteId}/config`, corps),
  révisions: (org: string, siteId: string) =>
    appel<{ revisions: RévisionConfig[]; error?: string }>(
      'GET',
      `/api/organizations/${org}/sites/${siteId}/config/revisions`,
    ),
  épingler: (org: string, siteId: string, backupId: string, pinned: boolean) =>
    appel<{ backupId: string; pinned: boolean; error?: string }>(
      'PATCH',
      `/api/organizations/${org}/sites/${siteId}/backups/${backupId}/pin`,
      { pinned },
    ),
  /** Adresse de téléchargement : le navigateur la suit directement, le flux ne passe pas par fetch. */
  urlTéléchargement: (org: string, siteId: string, backupId: string, composant: string) =>
    `/api/organizations/${org}/sites/${siteId}/backups/${backupId}/download/${composant}`,
  restaurer: (org: string, siteId: string, revisionId: string) =>
    appel<RésultatConfig>(
      'POST',
      `/api/organizations/${org}/sites/${siteId}/config/revisions/${revisionId}/restore`,
    ),
};

// --- Sauvegardes -------------------------------------------------------------------------------

export type MéthodeSauvegarde = 'restic' | 'streamed';
export type ComposantSauvegarde = 'content' | 'database' | 'config';

export interface Sauvegarde {
  id: string;
  /** Épinglée : conservée sans limite de temps, hors d'atteinte de la rétention. */
  pinned: boolean;
  startedAt: string;
  finishedAt: string | null;
  status: 'running' | 'success' | 'failed';
  type: 'scheduled' | 'manual' | 'pre_update';
  method: MéthodeSauvegarde;
  sizeBytes: number | null;
  resticSnapshotId: string | null;
}

export interface VérificationDépôt {
  supported: boolean;
  ok: boolean;
  snapshots: number | null;
  output: string;
  error?: string;
}

export interface RésultatRestauration {
  restored: ComposantSauvegarde[];
  restartError: string | null;
  log: string[];
  error?: string;
}

export const sauvegardes = {
  lister: (org: string, siteId: string) =>
    appel<{ backups: Sauvegarde[]; method: MéthodeSauvegarde; error?: string }>(
      'GET',
      `/api/organizations/${org}/sites/${siteId}/backups`,
    ),
  journal: (org: string, siteId: string, backupId: string) =>
    appel<{ backup: Sauvegarde & { log: string }; error?: string }>(
      'GET',
      `/api/organizations/${org}/sites/${siteId}/backups/${backupId}`,
    ),
  lancer: (org: string, siteId: string) =>
    appel<{ queued: boolean; error?: string }>(
      'POST',
      `/api/organizations/${org}/sites/${siteId}/backups`,
    ),
  vérifier: (org: string, siteId: string) =>
    appel<VérificationDépôt>('POST', `/api/organizations/${org}/sites/${siteId}/backups/check`),
  épingler: (org: string, siteId: string, backupId: string, pinned: boolean) =>
    appel<{ backupId: string; pinned: boolean; error?: string }>(
      'PATCH',
      `/api/organizations/${org}/sites/${siteId}/backups/${backupId}/pin`,
      { pinned },
    ),
  /** Adresse de téléchargement : le navigateur la suit directement, le flux ne passe pas par fetch. */
  urlTéléchargement: (org: string, siteId: string, backupId: string, composant: string) =>
    `/api/organizations/${org}/sites/${siteId}/backups/${backupId}/download/${composant}`,
  restaurer: (
    org: string,
    siteId: string,
    corps: {
      backupId: string;
      confirmSiteName: string;
      components?: ComposantSauvegarde[];
    },
  ) =>
    appel<RésultatRestauration>('POST', `/api/organizations/${org}/sites/${siteId}/restore`, corps),
};

// --- Mises à jour ------------------------------------------------------------------------------

export type NatureMiseÀJour = 'major' | 'minor' | 'patch' | 'none';

export type ÉtatMiseÀJour =
  | 'pending_approval'
  | 'queued'
  | 'prechecks'
  | 'backing_up'
  | 'updating'
  /** Installée, en attente d'un redémarrage qui n'appartient pas à Vigie. */
  | 'awaiting_restart'
  | 'verifying'
  | 'success'
  | 'failed'
  | 'rolled_back'
  | 'interrupted';

export interface VersionsDisponibles {
  currentVersion: string;
  latest: { version: string; kind: NatureMiseÀJour } | null;
  latestWithinMajor: { version: string; kind: NatureMiseÀJour } | null;
  error?: string;
}

export interface Préchecks {
  ok: boolean;
  failures: string[];
  details: Record<string, string>;
  error?: string;
}

export interface RunMiseÀJour {
  id: string;
  siteId: string;
  fromVersion: string;
  toVersion: string;
  status: ÉtatMiseÀJour;
  log: string;
  rolledBack: boolean;
  previousImageTag: string | null;
  batchId: string | null;
  startedAt: string;
  finishedAt: string | null;
  heartbeatAt: string | null;
}

export const misesÀJour = {
  disponibles: (org: string, siteId: string) =>
    appel<VersionsDisponibles>(
      'GET',
      `/api/organizations/${org}/sites/${siteId}/updates/available`,
    ),
  préchecks: (org: string, siteId: string, targetVersion: string) =>
    appel<Préchecks>('POST', `/api/organizations/${org}/sites/${siteId}/updates/prechecks`, {
      targetVersion,
    }),
  lancer: (org: string, siteId: string, targetVersion: string) =>
    appel<{ runId: string; status: ÉtatMiseÀJour; error?: string }>(
      'POST',
      `/api/organizations/${org}/sites/${siteId}/updates`,
      { targetVersion },
    ),
  runs: (org: string, siteId: string) =>
    appel<{ runs: RunMiseÀJour[]; error?: string }>(
      'GET',
      `/api/organizations/${org}/sites/${siteId}/updates`,
    ),
  approuver: (org: string, siteId: string, runId: string) =>
    appel<{ runId: string; status: ÉtatMiseÀJour; error?: string }>(
      'POST',
      `/api/organizations/${org}/sites/${siteId}/updates/${runId}/approve`,
    ),
  revenirEnArrière: (org: string, siteId: string, runId: string, reason: string) =>
    appel<{
      runId: string;
      status: ÉtatMiseÀJour;
      healthy: boolean;
      log: string[];
      error?: string;
    }>('POST', `/api/organizations/${org}/sites/${siteId}/updates/${runId}/rollback`, { reason }),
  /** « J'ai redémarré » : Vigie vérifie, elle ne prend pas la déclaration pour argent comptant. */
  validerRedémarrage: (org: string, siteId: string, runId: string) =>
    appel<{
      status: ÉtatMiseÀJour;
      fromVersion: string;
      toVersion: string;
      error?: string;
    }>('POST', `/api/organizations/${org}/sites/${siteId}/updates/${runId}/confirm-restart`),
  abandonner: (org: string, siteId: string, runId: string, reason: string) =>
    appel<{ runId: string; status: ÉtatMiseÀJour; error?: string }>(
      'POST',
      `/api/organizations/${org}/sites/${siteId}/updates/${runId}/abandon`,
      { reason },
    ),
  lot: (org: string, siteIds: string[], targetVersion?: string) =>
    appel<{ batchId: string; runIds: string[]; error?: string }>(
      'POST',
      `/api/organizations/${org}/updates/batch`,
      targetVersion === undefined ? { siteIds } : { siteIds, targetVersion },
    ),
};

// --- Réglages ----------------------------------------------------------------------------------

export type PortéeDépôt = 'organization' | 'site';

export interface DépôtSauvegarde {
  id: string;
  scope: PortéeDépôt;
  siteId: string | null;
  endpoint: string;
  bucket: string;
  region: string;
  prefix: string;
  /** La clé d'accès identifie et s'affiche ; la clé secrète revient masquée. */
  accessKey: string;
  secretKey: string;
  updatedAt: string;
}

export interface CanalAlerte {
  id: string;
  name: string;
  url: string;
  siteId: string | null;
  events: string[] | null;
  enabled: boolean;
  /** `vigie` ou `slack` : Slack refuse tout JSON qui n'est pas le sien. */
  format: 'vigie' | 'slack';
  /** Une signature est-elle posée ? Le secret lui-même n'est jamais renvoyé. */
  signed: boolean;
}

export interface FenêtreMiseÀJour {
  name: string;
  startHour: number;
  endHour: number;
  timeZone: string;
}

export interface Réglages {
  updateWindow: FenêtreMiseÀJour;
  storage: DépôtSauvegarde[];
  globalStorageConfigured: boolean;
  webhooks: CanalAlerte[];
  error?: string;
}

export interface TestDépôt {
  ok: boolean;
  step: 'write' | 'read' | 'delete' | 'done';
  message: string;
  error?: string;
}

export interface TestCanal {
  ok: boolean;
  status: number | null;
  error: string | null;
}

export const réglages = {
  lire: (org: string) => appel<Réglages>('GET', `/api/organizations/${org}/settings`),
  enregistrerDépôt: (org: string, corps: Record<string, unknown>) =>
    appel<{ id: string; error?: string; details?: unknown }>(
      'PUT',
      `/api/organizations/${org}/settings/storage`,
      corps,
    ),
  testerDépôt: (org: string, id: string) =>
    appel<TestDépôt>('POST', `/api/organizations/${org}/settings/storage/${id}/test`),
  supprimerDépôt: (org: string, id: string) =>
    appel<{ deleted: boolean; error?: string }>(
      'DELETE',
      `/api/organizations/${org}/settings/storage/${id}`,
    ),
  créerCanal: (org: string, corps: Record<string, unknown>) =>
    appel<{ id: string; error?: string }>(
      'POST',
      `/api/organizations/${org}/settings/webhooks`,
      corps,
    ),
  modifierCanal: (org: string, id: string, corps: Record<string, unknown>) =>
    appel<{ id: string; error?: string }>(
      'PATCH',
      `/api/organizations/${org}/settings/webhooks/${id}`,
      corps,
    ),
  supprimerCanal: (org: string, id: string) =>
    appel<{ deleted: boolean; error?: string }>(
      'DELETE',
      `/api/organizations/${org}/settings/webhooks/${id}`,
    ),
  testerCanal: (org: string, id: string) =>
    appel<TestCanal>('POST', `/api/organizations/${org}/settings/webhooks/${id}/test`),
  enregistrerFenêtre: (org: string, corps: FenêtreMiseÀJour) =>
    appel<{ ok: boolean; error?: string }>(
      'PATCH',
      `/api/organizations/${org}/settings/update-window`,
      corps,
    ),
};
